import api from './api';
import config from '../config/environments';

// Use mock API based on environment configuration
const USE_MOCK_API = config.mockApi;

// API endpoints for authentication
const API_ENDPOINTS = {
  LOGIN: '/admin/auth/login',
  REFRESH_TOKEN: '/admin/auth/refresh-token',
  LOGOUT: '/admin/auth/logout',
  FORGOT_PASSWORD: '/admin/auth/forgot-password',
  RESET_PASSWORD: '/admin/auth/reset-password',
  CHANGE_PASSWORD: '/admin/auth/change-password',
  SESSIONS: '/admin/auth/sessions'
};

export interface LoginRequest {
  email: string;
  password: string;
  deviceId?: string;
}

export interface AdminUser {
  id?: string; // Keep for backward compatibility
  _id: string; // API returns _id instead of id
  email: string;
  firstName: string;
  lastName: string;
  role: 'SYSTEM_ADMIN' | 'ADMIN';
  passwordChangeRequired: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponse {
  user: AdminUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  passwordChangeRequired: boolean;
}

export interface RefreshTokenRequest {
  refreshToken: string;
  deviceId: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LogoutRequest {
  refreshToken: string;
  deviceId: string;
  allDevices?: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface Session {
  id: string;
  userId: string;
  deviceInfo: {
    deviceId: string;
    deviceName?: string;
    browser?: string;
    os?: string;
    ip?: string;
    lastUsed?: string;
  };
  isValid: boolean;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // Generate or get device ID
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = `device_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem('device_id', deviceId);
    }
    
    // Use mock API if enabled
    if (USE_MOCK_API) {
      console.log('Using mock login for testing');
      
      // Check if credentials match our test admin user
      if (credentials.email === 'admin@wine.com' && credentials.password === 'admin123') {
        // Create mock response
        const mockResponse: LoginResponse = {
          user: {
            id: '1',
            _id: '1', // Add _id to match API response format
            email: credentials.email,
            firstName: 'Admin',
            lastName: 'User',
            role: 'SYSTEM_ADMIN',
            passwordChangeRequired: false,
            active: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          accessToken: 'mock-access-token-for-testing-purposes',
          refreshToken: 'mock-refresh-token-for-testing-purposes',
          expiresIn: 3600,
          passwordChangeRequired: false
        };
        
        // Store tokens and user info
        localStorage.setItem('access_token', mockResponse.accessToken);
        localStorage.setItem('refresh_token', mockResponse.refreshToken);
        localStorage.setItem('token_expiry', (Date.now() + mockResponse.expiresIn * 1000).toString());
        localStorage.setItem('user', JSON.stringify(mockResponse.user));
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        return mockResponse;
      } else {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));
        throw new Error('Invalid credentials');
      }
    }
    
    // Real API implementation
    try {
      console.log(`Attempting login with endpoint: ${API_ENDPOINTS.LOGIN}`);
      
      // Create the payload with deviceId
      const payload = {
        email: credentials.email,
        password: credentials.password,
        deviceId: deviceId
      };
      
      // For debugging, log the full request
      console.log('Login request payload:', payload);
      
      // Send the request with the deviceId included
      const response = await api.post<LoginResponse>(API_ENDPOINTS.LOGIN, payload);
      
      console.log('Login response:', response.data);
      
      // If successful, store tokens and user info
      localStorage.setItem('access_token', response.data.accessToken);
      localStorage.setItem('refresh_token', response.data.refreshToken);
      localStorage.setItem('token_expiry', (Date.now() + response.data.expiresIn * 1000).toString());
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      console.log('Login successful');
      return response.data;
    } catch (error: any) {
      console.error('Login failed:', error);
      
      // Provide more detailed error information
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      
      throw error;
    }
  },
  
  /**
   * Refresh the access token using the refresh token
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    const refreshToken = localStorage.getItem('refresh_token');
    const deviceId = localStorage.getItem('device_id') || 
      `device_${Math.random().toString(36).substring(2, 15)}`;
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    // Use mock API if enabled
    if (USE_MOCK_API) {
      console.log('Using mock token refresh');
      
      // Create mock response
      const mockResponse: RefreshTokenResponse = {
        accessToken: 'mock-access-token-refreshed',
        refreshToken: 'mock-refresh-token-refreshed',
        expiresIn: 3600
      };
      
      // Store new tokens
      localStorage.setItem('access_token', mockResponse.accessToken);
      localStorage.setItem('refresh_token', mockResponse.refreshToken);
      localStorage.setItem('token_expiry', (Date.now() + mockResponse.expiresIn * 1000).toString());
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return mockResponse;
    }
    
    // Real API implementation
    try {
      const response = await api.post<RefreshTokenResponse>(API_ENDPOINTS.REFRESH_TOKEN, {
        refreshToken,
        deviceId
      });
      
      // Store new tokens
      localStorage.setItem('access_token', response.data.accessToken);
      localStorage.setItem('refresh_token', response.data.refreshToken);
      localStorage.setItem('token_expiry', (Date.now() + response.data.expiresIn * 1000).toString());
      
      return response.data;
    } catch (error) {
      console.error('Token refresh failed');
      // Don't call logout here to avoid circular calls
      // Let the caller handle the logout
      throw error;
    }
  },
  
  /**
   * Check if token is expired and refresh if needed
   */
  checkAndRefreshToken: async (): Promise<boolean> => {
    const tokenExpiry = localStorage.getItem('token_expiry');
    
    if (!tokenExpiry) {
      return false;
    }
    
    const expiryTime = parseInt(tokenExpiry, 10);
    const currentTime = Date.now();
    
    // If token is expired or about to expire in the next 5 minutes
    if (expiryTime - currentTime < 5 * 60 * 1000) {
      try {
        await authService.refreshToken();
        return true;
      } catch (error) {
        return false;
      }
    }
    
    return true;
  },

  /**
   * Logout the current user
   */
  logout: async (logoutAll: boolean = false) => {
    const refreshToken = localStorage.getItem('refresh_token');
    const deviceId = localStorage.getItem('device_id') || '';
    
    // If not using mock API and we have a refresh token, call the logout endpoint
    if (!USE_MOCK_API && refreshToken) {
      try {
        await api.post(API_ENDPOINTS.LOGOUT, {
          refreshToken,
          deviceId,
          allDevices: logoutAll
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with local logout even if API call fails
      }
    }
    
    // Clear all auth-related data from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user');
    
    // Only remove device_id if logging out from all devices
    if (logoutAll) {
      localStorage.removeItem('device_id');
    }
    
    // Redirect to login page
    window.location.href = '/login';
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Get current user
   */
  getCurrentUser: (): AdminUser | null => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  },

  /**
   * Check if current user has admin role
   */
  isAdmin: (): boolean => {
    const user = authService.getCurrentUser();
    
    // Debug: Log the user role for debugging
    console.log('User role check:', { 
      userRole: user?.role, 
      user: user 
    });
    
    if (!user) return false;
    
    // According to API spec, role is either 'SYSTEM_ADMIN' or 'ADMIN'
    return user.role === 'SYSTEM_ADMIN' || user.role === 'ADMIN';
  },
  
  /**
   * Check if current user has system admin role
   */
  isSystemAdmin: (): boolean => {
    const user = authService.getCurrentUser();
    return user?.role === 'SYSTEM_ADMIN';
  },

  /**
   * Request password reset for a user
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const payload: ForgotPasswordRequest = { email };
    const response = await api.post(API_ENDPOINTS.FORGOT_PASSWORD, payload);
    return response.data;
  },

  /**
   * Reset password using token
   */
  resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
    const payload: ResetPasswordRequest = { token, newPassword };
    const response = await api.post(API_ENDPOINTS.RESET_PASSWORD, payload);
    return response.data;
  },

  /**
   * Change password for current user
   */
  changePassword: async (userId: string, currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const payload: ChangePasswordRequest = { currentPassword, newPassword };
    const response = await api.post(`${API_ENDPOINTS.CHANGE_PASSWORD}/${userId}`, payload);
    return response.data;
  },

  /**
   * Get all active sessions for the current user
   */
  getSessions: async (): Promise<Session[]> => {
    const response = await api.get(API_ENDPOINTS.SESSIONS);
    return response.data;
  },

  /**
   * Terminate a specific session
   */
  terminateSession: async (sessionId: string): Promise<{ message: string }> => {
    const response = await api.delete(`${API_ENDPOINTS.SESSIONS}/${sessionId}`);
    return response.data;
  }
};

export default authService;
